<template>
  <div>
    <v-breadcrumbs :items="breadcrumbItems"/>

    <v-card  v-if="event">
      <Header :event="event"/>
      <v-row>
        <v-col cols="7">
          Other stats
        </v-col>
        <v-col cols="5">
          <InsightsSummary :event="event" ref="insights" />
        </v-col>
      </v-row>
      <v-card-title class="headline">Insights for {{event.name}}</v-card-title>

      <v-card-text>      
        <p>
          Key metrics are calculated weekly, based on Monday - Sunday weeks. 
          Note that insights are always at least one day behind. 
        </p>
        <p>
          Insights are calculated based on standardized metrics, allowing a consistent measurement across events and comparison with industry standards (coming soon).
        </p>
      </v-card-text>

      <v-alert v-if="!eventUtil.isProPlan()" type="info" tile>
        <div class="d-flex flex-row align-content-center">
          <p class="grow mb-0"><router-link class="white--text" :to="{name: 'eventmanagerLicense', params: {id:event.id}}">Upgrade to a PRO plan</router-link> to access insights.</p>
          <v-btn class="shrink white--text" :to="{name: 'eventmanagerLicense', params: {id:event.id}}">Upgrade</v-btn>
        </div>
      </v-alert>

      <v-container v-if="insights">
        <h4>Period <span v-if="snapshot">{{ snapshot.period_type }} {{ snapshot.period }}</span></h4>
        <p v-if="snapshot">
          Based on period running from 
          {{snapshot.from | localDate('D', true) }}
          till
          {{snapshot.till | localDate('D', true) }}
          <v-dialog v-if="insights.snapshots.length > 1" v-model="showPeriodDialog" max-width="400px">
            <template v-slot:activator="{ on }">
              <v-btn text small color="primary" class="" v-on="on">select</v-btn>
            </template>
            <v-card>
              <v-card-title>Select Period</v-card-title>
              <v-card-text>
                Select the period to view.
              </v-card-text>
              <v-divider></v-divider>
              <v-list>
                <template v-for="(item, idx) in insights.snapshots.filter(x => x.scores != null)">
                  <v-list-item :key="idx">
                    <v-list-item-content>
                    <v-list-item-title>{{item.from | localDate('D', true) }} - {{item.till | localDate('D', true) }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn text color="primary" @click="snapshot=item;showPeriodDialog = false;"> {{$t('shared.select')}}</v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider  :key="`sep-${idx}`"/>
                </template>
              </v-list>
              <v-card-actions>
                <v-btn color="secondary" text @click="showPeriodDialog = false">{{$t('shared.cancel')}}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>   
          .
        </p>
        <p v-if="snapshot" class="text-muted">
          Metrics are calculated on {{ snapshot.calc_ts | localDate('ddd L LTS') }}
        </p>
        <v-alert v-if="snapshot && snapshot.partial" type="info" text>
          Reporting is based on a partial week (because either the start or end of the event was within this period). 
          This might impact the results of this period compared to other periods.
        </v-alert>
        <p v-if="!snapshot">
          Please wait until one week after the event has started for the first metrics to start showing.
        </p>
        <div v-if="snapshot && snapshot.scores">
          <h4>Key Metrics</h4>
          <v-btn color="primary" text @click="downloadReport('insights-improvers', `${event.name}-insights-improvers`)">Download</v-btn>
        </div>
        <v-row v-if="snapshot && snapshot.scores">
          <v-col cols="12" sm="4" md="4">
            <v-card class="white--text" color="primary darken-4">
              <v-card-title class="white--text ">
                Engagement Score
                <v-spacer/>
                <v-btn icon class="ma-0" @click="engagementScoreExpanded = !engagementScoreExpanded">
                  <v-icon color="white">{{ engagementScoreExpanded ? 'fa fal fa-chevron-circle-up' : 'fa fal fa-chevron-circle-down' }}</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="white--text pb-0" style="opacity:.7;">
                Overall engagement score with this event.
              </v-card-text>
              <div v-if="snapshot && snapshot.scores.engagement_score">
                <v-card-title class="subtitle">
                  <span>{{ snapshot.scores.engagement_score }}%</span>
                  <span v-if="engagementScoreExpanded" class="ms-1">(out of {{ snapshot.scores.engagement_count }})</span>
                  <simple-tooltip color="white" bottom tooltip-color="info" icon-class="ms-2" :text="`Percentage of participants engaged with this event. Out of ${snapshot.scores.engagement_count} participants who have joined this event.`"/>
                </v-card-title>
                <!-- <v-card-title v-else class="subtitle">n/a</v-card-title> -->
              </div>
              <div v-if="false && engagementScoreExpanded">
                <p class="subtitle">Participation: {{ snapshot.scores.participation_score }}%</p>
                <p class="subtitle">Social: {{ snapshot.scores.social_score }}%</p>
              </div>
              <v-sparkline :labels="engagementScoresSparklineLabels" :value="engagementScoresSparklineValues" type="trend" smooth />
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <v-card class="white--text" color="primary darken-3">
              <v-card-title class="white--text ">
                Activity Score
                <v-spacer/>
                <v-btn icon class="ma-0" @click="activityScoreExpanded = !activityScoreExpanded">
                  <v-icon color="white">{{ activityScoreExpanded ? 'fa fal fa-chevron-circle-up' : 'fa fal fa-chevron-circle-down' }}</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="white--text pb-0" style="opacity:.7;">
                Percentage increase in activity by all participants.
              </v-card-text>
              <div v-if="snapshot && snapshot.pts_vs_baseline">
                <v-card-title class="subtitle">
                  <span>{{ snapshot.pts_vs_baseline > 1 ? '+' : '' }}</span>
                  <span>{{ (snapshot.pts_vs_baseline - 1) * 100 | int }}%</span>
                  <simple-tooltip color="white" bottom tooltip-color="info" icon-class="ms-2" text="Average points per period by all participants, compared with baseline."/>
                </v-card-title>
              </div>
              <v-card-title v-else class="subtitle">Not available (yet)</v-card-title>
              <v-sparkline :labels="activityScoresSparklineLabels" :value="activityScoresSparklineValues" type="trend" smooth />
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <v-card class="white--text" color="primary darken-2">
              <v-card-title class="white--text ">
                Improvers Score
                <simple-tooltip v-if="false" color="white" icon-class="ms-2" bottom text="Percentage of participants moving more or reaching daily healthy target. Compared with performance in week before event started, or scaled first week if no other data is available."/>
                <v-spacer />
                <v-btn icon color="white" @click="moveScoreExpanded = !moveScoreExpanded">
                  <v-icon>{{ moveScoreExpanded ? 'fa fal fa-chevron-circle-up' : 'fa fal fa-chevron-circle-down' }}</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="white--text pb-0" style="opacity:.7;">
                Percentage participants who move more.
              </v-card-text>
              <v-card-title v-if="snapshot && snapshot.scores.move_score" class="subtitle">
                <span>{{ snapshot.scores.move_score }}% </span>
                <span v-if="moveScoreExpanded" class="ms-1">(out of {{ snapshot.scores.move_count }})</span>
                <simple-tooltip color="white" bottom tooltip-color="info" icon-class="ms-2" text="Percentage of participants moving more (or reaching daily healthy target). Compared with performance in week before event started, or scaled first week if no other data is available."/>
              </v-card-title>
              <v-card-title v-else class="subtitle">Not available (yet)</v-card-title>
              <v-card-text v-if="moveScoreExpanded" class="white--text">
                
              </v-card-text>
              <div v-if="moveScoreExpanded" class="">
                <v-card-title class="pb-0">
                  Significant Improvers score
                </v-card-title>
                <v-card-title class="subtitle">
                  <span>{{ snapshot.scores.tier_score }}% </span>
                  <span v-if="moveScoreExpanded" class="ms-1">(out of {{ snapshot.scores.tier_count }})</span>
                  <simple-tooltip color="white" bottom tooltip-color="info" icon-class="ms-2" text="Percentage of participants improved a league (or already at max league). Compared with performance in week before event started."/>
                </v-card-title>
              </div>
              <v-sparkline :labels="moveScoresSparklineLabels" :value="moveScoresSparklineValues" type="trend" smooth />
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <v-container v-if="snapshot && snapshot.podiums" class="mt-0">
        <h4>Podiums</h4>
        <p>Most popular activity types for participants of this event.</p>
        <p>
          <v-btn color="primary" text @click="downloadReport('insights-podiums', `${event.name}-insights-podiums`)">Download</v-btn>
        </p>
        <v-row>
          <v-col v-for="(item, idx) in snapshot.podiums" :key="idx" cols="12" sm="4" md="4">
            <v-card class="white--text" :color="`accent darken-${idx}`">
              <v-card-title class="white--text force-one-line" :title="$helpers.getActivityTypeLabel(item.activity_type, event)">
                <!-- <v-icon color="white" small class="me-2">{{ $helpers.getActivityIcon(item.activity_type) }}</v-icon> -->
                {{ $helpers.getActivityTypeLabel(item.activity_type, event) }}
              </v-card-title>
              <v-card-text class="white--text">{{ item.stats.user_count}} participants</v-card-text>
              <!-- <v-card-text class="white--text">Supported: {{ item.stats.supported}}?</v-card-text> -->
              <v-divider style="border-color: #ffffff33;" />
              <v-card-text>
              <div v-for="( profile, pos) in item.profiles" :key="pos" class="white--text pb-0 pt-2">#{{ pos+1 }}. {{ profile.name }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <v-container v-if="snapshot" class="mt-0">
        <h4>Leagues</h4>
        <p>Participants per league. Leagues are calculated based on the average points per day.</p>
        <p>
          <v-btn color="primary" text @click="downloadReport('insights-leagues', `${event.name}-insights-leagues`)">Download</v-btn>
        </p>
        <v-row  v-if="snapshot.assessment && snapshot.assessment.tiers">
          <v-col v-for="(item, idx) in snapshot.assessment.tiers" :key="idx" cols="12" sm="4" md="3">
            <v-card class=""  :style="{'border-color': `var(--v-league-${item.tier}-base)`, 'border-style': 'solid', 'border-width': '5px'}">
              <v-card-title class="force-one-line pb-0" >
                {{ item.tier | league }} 
              </v-card-title>
              <v-card-text class="pb-0" style="opacity:.7;">
                <span v-if="item.tier == 1">0 - 3k pts/day</span>
                <span v-if="item.tier == 2">3k - 6k pts/day</span>
                <span v-if="item.tier == 3">6k - 10k pts/day</span>
                <span v-if="item.tier == 4">10k+ pts/day</span>
              </v-card-text>
              <v-card-text class="">{{ item.count}} participants</v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <p v-else>
          This data is not available yet. Please check back in a few days.
        </p>
      </v-container>

      <v-card-text v-if="insights && false">
        <h3>Week-by-week breakdown</h3>
        <div v-for="(item, idx) in insights.snapshots" :key="idx" class="d-flex " :set="total = item.assessment.tiers['1'].count + item.assessment.tiers['2'].count + item.assessment.tiers['3'].count + item.assessment.tiers['4'].count">
          <div class="d-flex px-2" style="flex: 200px;">
            <span v-if="idx == 0">Week before start</span>
            <strong v-else>Week {{ idx }}:&nbsp;</strong>
            <div>
              {{item.from | localDate('D', true) }}
              -
              {{item.till | localDate('D', true) }}
              <span v-if="item.partial">(partial)</span>
            </div>
          </div>
          <div class="d-flex px-2" style="flex: 50px;">
            <span v-if="item.scores && item.scores.engagement_score">{{item.scores.engagement_score }}</span>
          </div>
          <div class="d-flex px-2" style="flex: 50px;">
            <span v-if="item.scores && item.scores.move_score">{{item.scores.move_score }}</span>
          </div>
          <div class="d-flex green lighten-2 align-center" :style="`flex: ${item.assessment.tiers['1'].count};`">
            <!-- {{ item.assessment.tiers['1'].count }} -->
          </div>
          <div class="d-flex blue lighten-2" :style="`flex: ${item.assessment.tiers['2'].count};`">
            <!-- {{ item.assessment.tiers['2'].count }} -->
          </div>
          <div class="d-flex red lighten-2" :style="`flex: ${item.assessment.tiers['3'].count};`">
            <!-- {{ item.assessment.tiers['3'].count }} -->
          </div>
          <div class="d-flex grey darken-2" :style="`flex: ${item.assessment.tiers['4'].count};`">
            <!-- {{ item.assessment.tiers['4'].count }} -->
          </div>
        </div>
      </v-card-text>

    </v-card>
  </div>      
</template>

<script>
import { mapGetters } from "vuex";
import eventManagerService from "@/services/eventManagerService";
import { EventBus } from '@/plugins/eventbus.js';
import { DateTime } from 'luxon'
import siteData from '@/data/site.json'
import Header from './_Header.vue'
import EventUtil from "@/util/eventUtil";
import InsightsSummary from '@/components/eventmanager/InsightsSummary.vue';
import SimpleTooltip from '@/components/SimpleTooltip.vue';

export default {
  name: "Insights",
  components: {
    Header,
    SimpleTooltip,
    InsightsSummary,
  },
  data() {
    return {
      event: null,
      provider: null,
      model: {},
      stats: null,
      insights: null,
      snapshot: null,
      siteData: siteData,
      races: [],
      showPeriodDialog: false,
      engagementScoreExpanded: false,
      activityScoreExpanded: false,
      moveScoreExpanded: false,
      headers: [
        { text: 'Name', sortable: true, value: 'name',},
        { text: 'Participants', sortable: true, value: 'participants',},
        { text: 'Results', sortable: true, value: 'results',},
        { text: 'Completed', sortable: true, value: 'completed',},
        { text: 'Actions', value: 'actions', sortable: false },
      ],

    };
  },
  async mounted() {
    await this.getProfile();
    
    EventBus.$on('login-state-change', async user => {
      await this.getProfile();
    });
  },
  methods: {
    async getEvent(id) {
      this.event = (await eventManagerService.get(id)).data;
      this.stats = (await eventManagerService.getStats(id)).data;
      await this.getInsights();
    },
    async getInsights(date) {
      this.insights = (await eventManagerService.getInsights(this.event.id, date || this.$route.query.date)).data;
      this.snapshot = this.insights.latest;
      console.log('STATS snapshots', this.insights);
      this.$refs.insights.load();
    },
    async getProfile() {
      if (this.user) {
        await this.getEvent(this.$route.params.id);
      }
    },
    async downloadReport(reportName, filename) {
      var fromDate = DateTime.fromISO(this.snapshot.from, { zone: this.event.timezone }).startOf('day').toISODate();
      var tillDate = DateTime.fromISO(this.snapshot.till, { zone: this.event.timezone }).startOf('day').toISODate();
      console.log('/// date', fromDate, tillDate);

      try{
        const response = await eventManagerService.exportReport(this.event.id, reportName, /*group:*/null, fromDate, tillDate);
        this.downloadReportData(response, `${filename}-${this.snapshot.period_type}-${this.snapshot.period}`);
      }
      catch {
        this.$helpers.toastResponse(this, { status: "ERROR", msg: "No permissions to view report."});
      }
    },
    async downloadReportData(response, filename) {
      console.log('// report response', response);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = filename.replace(".", "_")
        link.click()
        URL.revokeObjectURL(link.href)
    },
    // getStatsForRace(raceId) {
    //   return this.stats && this.stats.participants_by_race && this.stats.participants_by_race[raceId];
    // },

  },
  computed: {
    breadcrumbItems() {
      return !this.event ? [] : [
        { text: 'Event Manager', exact: true, to: { name: 'eventmanager' } },
        { text: this.event.name, exact: true, to: { name: 'eventmanagerView', params: {id: this.event.id}} },
        { text: `Insights`, disabled: true },
      ];
    },
    eventUtil() {
      return new EventUtil(this, this.event);
    },
    reportsAvailable() {
      return this.eventUtil.isProPlan();
    },

    
    engagementScoresSparklineLabels() {
      return this.insights == null || this.insights.snapshots == null
             ? []
             : [ 'Start', ...this.insights.snapshots.filter(x => x.scores && x.scores.engagement_score).map(x => this.$options.filters.localDate(x.from, 'XS'))];
    },
    engagementScoresSparklineValues() {
      return this.insights == null  || this.insights.snapshots == null
             ? []
             : [ 0, ...this.insights.snapshots.filter(x => x.scores && x.scores.engagement_score).map(x => x.scores.engagement_score)];
    },
    activityScoresSparklineLabels() {
      return this.insights == null || this.insights.snapshots == null
             ? []
             : [ 'Start', ...this.insights.snapshots.filter(x => x.pts_vs_baseline).map(x => this.$options.filters.localDate(x.from, 'XS'))];
    },
    activityScoresSparklineValues() {
      return this.insights == null  || this.insights.snapshots == null
             ? []
             : [ 0, ...this.insights.snapshots.filter(x => x.pts_vs_baseline).map(x => x.pts_vs_baseline)];
    },
    moveScoresSparklineLabels() {
      return this.insights == null  || this.insights.snapshots == null
             ? []
             : [ 'Start', ...this.insights.snapshots.filter(x => x.scores && x.scores.move_score).map(x => this.$options.filters.localDate(x.from, 'XS'))];
    },
    moveScoresSparklineValues() {
      return this.insights == null  || this.insights.snapshots == null
             ? []
             : [ 0, ...this.insights.snapshots.filter(x => x.scores && x.scores.move_score).map(x => x.scores.move_score)];
    },

    ...mapGetters({
      user: "user"
    })
  },
};
</script>
<style lang="scss">
</style>

